import type { DocsBlock } from '@nadoutong/contracts';

/**
 * Card-like block types that sat in multi-column grids on the
 * handwritten page. Consecutive visible siblings of one of these kinds
 * share a CSS auto-fit grid so a pair reads 2-up and a compact triple
 * can 3-up, without the renderer learning section ids or copy.
 *
 * param_table / steps stay full-width: they were either already
 * full-width or internally gridded (steps). Note/warning callouts are
 * banners, not cards — grouping those would 2-up stacked notes. A
 * lone snippet stays full-width; consecutive snippets share a wider
 * min-column than the 20rem cards so they only 2-up on a genuinely
 * wide content column (the old handwritten `2xl:grid-cols-2`).
 */
export type DocsCardFlowKind = 'key_values' | 'checklist' | 'link' | 'callout_fact' | 'snippet';

export type DocsBlockFlowItem =
  | { readonly kind: 'solo'; readonly block: DocsBlock; readonly index: number }
  | {
      readonly kind: 'grid';
      readonly group: DocsCardFlowKind;
      readonly items: readonly { readonly block: DocsBlock; readonly index: number }[];
    };

/**
 * Wide cards (family / provider / checklist): 20rem min so a pair is
 * 2-up in the docs column and a third only fits on a 2xl-wide column.
 * Compact action links: 12rem min so three sit 3-up from ~md, matching
 * the handwritten action-link row.
 *
 * Snippet pairs (Python SDK + curl) used `2xl:grid-cols-2` on the
 * handwritten page. Tailwind `2xl` is 1536px. At that viewport the
 * padded docs content column is:
 *   1536
 *   − 16rem sidebar (`w-64`)
 *   − 4rem main padding (`p-8` × 2)
 *   − 11rem TOC + 1rem grid gap (`xl:grid-cols-[11rem_minmax(0,1fr)]`)
 *   − 2.5rem Panel body padding (`p-5` × 2)
 *   = 61.5rem
 * Two columns with `gap-3` (0.75rem) therefore need a min track of
 * (61.5 − 0.75) / 2 = 30.375rem. 30rem is the nearest rem that still
 * 2-ups at 2xl (2 × 30 + 0.75 = 60.75 < 61.5) and stays one column at
 * xl (1280 → 45.5rem inner). Code wells keep their own `overflow-x-auto`.
 *
 * `auto-fit` collapses empty tracks so a pair stretches to fill the
 * row. `min(100%, …)` keeps a single column on a narrow container
 * instead of overflowing.
 */
export const DOCS_WIDE_CARD_GRID_CLASS =
  'grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(100%,20rem),1fr))]';

export const DOCS_COMPACT_CARD_GRID_CLASS =
  'grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(100%,12rem),1fr))]';

export const DOCS_SNIPPET_CARD_GRID_CLASS =
  'grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(100%,30rem),1fr))]';

export function docsCardFlowKind(block: DocsBlock): DocsCardFlowKind | null {
  switch (block.type) {
    case 'key_values':
    case 'checklist':
    case 'link':
    case 'snippet':
      return block.type;
    case 'callout':
      return block.tone === 'fact' ? 'callout_fact' : null;
    default:
      return null;
  }
}

export function docsCardGridClass(group: DocsCardFlowKind): string {
  if (group === 'link') return DOCS_COMPACT_CARD_GRID_CLASS;
  if (group === 'snippet') return DOCS_SNIPPET_CARD_GRID_CLASS;
  return DOCS_WIDE_CARD_GRID_CLASS;
}

/**
 * Collapse consecutive same-kind card-like blocks into one grid item.
 * A lone card, or any non-card type, stays a solo full-width block.
 * Order is document order; indices are positions in the input list
 * (the visible-block list the section already filtered).
 */
export function groupDocsBlockFlow(blocks: readonly DocsBlock[]): readonly DocsBlockFlowItem[] {
  const out: DocsBlockFlowItem[] = [];
  let i = 0;
  while (i < blocks.length) {
    const block = blocks[i];
    if (block === undefined) break;
    const group = docsCardFlowKind(block);
    if (group === null) {
      out.push({ kind: 'solo', block, index: i });
      i += 1;
      continue;
    }
    let end = i + 1;
    while (end < blocks.length) {
      const next = blocks[end];
      if (next === undefined || docsCardFlowKind(next) !== group) break;
      end += 1;
    }
    if (end === i + 1) {
      out.push({ kind: 'solo', block, index: i });
    } else {
      const items: { readonly block: DocsBlock; readonly index: number }[] = [];
      for (let k = i; k < end; k += 1) {
        const grouped = blocks[k];
        if (grouped === undefined) break;
        items.push({ block: grouped, index: k });
      }
      out.push({ kind: 'grid', group, items });
    }
    i = end;
  }
  return out;
}
